export type ProvenanceKind = 'models' | 'deterministic' | 'not_recorded';

export interface StageProvenanceModel {
  model: string;
  count: number;
  pct: number;
}

export interface StageProvenance {
  kind: ProvenanceKind;
  /** Only for kind === 'models'. Descending by count. */
  models?: StageProvenanceModel[];
  /** Column the distribution was read from, e.g. 'predictions.structure_model'. */
  source?: string;
  /** Short human reason, used for 'deterministic' / 'not_recorded'. */
  note?: string;
}

const DEFAULT_DETERMINISTIC_NOTE = 'no model — computed from stored values';
const DEFAULT_NOT_RECORDED_NOTE = 'provenance is not stored for this stage';

/**
 * Static stage → provenance classification.
 *
 * This is a statement about *where provenance is or is not stored* in the
 * intel-platform pipeline. It must never contain a model name; it only
 * classifies stages as deterministic or not_recorded when the API does not
 * supply a provenance blob.
 */
// Exported for tests and gallery fixtures; fast-refresh rule does not apply to data constants.
// eslint-disable-next-line react-refresh/only-export-components
export const STAGE_PROVENANCE_CLASSIFICATION: Record<string, StageProvenance> = {
  extracted: { kind: 'not_recorded', note: DEFAULT_NOT_RECORDED_NOTE },
  asset_identified: { kind: 'not_recorded', note: DEFAULT_NOT_RECORDED_NOTE },
  has_figure: { kind: 'deterministic', note: DEFAULT_DETERMINISTIC_NOTE },
  structure_extracted: {
    kind: 'not_recorded',
    source: 'predictions.structure_model',
    note: DEFAULT_NOT_RECORDED_NOTE,
  },
  measurable: {
    kind: 'not_recorded',
    source: 'predictions.structure_model',
    note: DEFAULT_NOT_RECORDED_NOTE,
  },
  condition_resolvable: {
    kind: 'not_recorded',
    source: 'predictions.condition_model',
    note: DEFAULT_NOT_RECORDED_NOTE,
  },
  has_resolve_date: { kind: 'deterministic', note: DEFAULT_DETERMINISTIC_NOTE },
  due: { kind: 'deterministic', note: DEFAULT_DETERMINISTIC_NOTE },
  graded: {
    kind: 'not_recorded',
    source: 'predictions.graded_by',
    note: DEFAULT_NOT_RECORDED_NOTE,
  },
};

function isValidProvenance(value: unknown): value is StageProvenance {
  if (!value || typeof value !== 'object') return false;
  const p = value as Partial<StageProvenance>;
  if (!p.kind || !['models', 'deterministic', 'not_recorded'].includes(p.kind)) {
    return false;
  }
  if (p.kind === 'models') {
    if (!Array.isArray(p.models) || p.models.length === 0) return false;
    for (const m of p.models) {
      if (
        !m ||
        typeof m.model !== 'string' ||
        typeof m.count !== 'number' ||
        m.count < 0 ||
        typeof m.pct !== 'number'
      ) {
        return false;
      }
    }
  }
  return true;
}

function resolveProvenance(
  stageKey: string,
  provenance: StageProvenance | undefined
): StageProvenance {
  if (isValidProvenance(provenance)) return provenance;
  return (
    STAGE_PROVENANCE_CLASSIFICATION[stageKey] ?? {
      kind: 'not_recorded',
      note: DEFAULT_NOT_RECORDED_NOTE,
    }
  );
}

function stripVendorPrefix(model: string): string {
  const idx = model.indexOf('/');
  return idx >= 0 ? model.slice(idx + 1) : model;
}

function formatModelPct(count: number, total: number): string {
  if (total <= 0) return '0%';
  const pct = (count / total) * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
}

interface FunnelStageProvenanceProps {
  stageKey: string;
  provenance?: StageProvenance;
}

export default function FunnelStageProvenance({
  stageKey,
  provenance,
}: FunnelStageProvenanceProps) {
  const resolved = resolveProvenance(stageKey, provenance);

  if (resolved.kind === 'deterministic') {
    return (
      <span
        className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
        title={resolved.note ?? DEFAULT_DETERMINISTIC_NOTE}
      >
        deterministic
      </span>
    );
  }

  if (resolved.kind === 'not_recorded') {
    return (
      <span
        className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
        title={resolved.note ?? DEFAULT_NOT_RECORDED_NOTE}
      >
        not recorded
      </span>
    );
  }

  const models = [...(resolved.models ?? [])].sort((a, b) => b.count - a.count);
  const total = models.reduce((sum, m) => sum + m.count, 0);
  const inline = models.slice(0, 3);
  const remaining = models.slice(3);

  let tooltip = models
    .map((m) => `${m.model} ${formatModelPct(m.count, total)}`)
    .join(' · ');
  if (resolved.note) {
    tooltip += `\n\n${resolved.note}`;
  }

  return (
    <span
      className="inline-block mt-0.5 text-[10px] text-gray-500 dark:text-gray-400"
      title={tooltip}
    >
      {inline.map((m, i) => (
        <span key={m.model}>
          {i > 0 && ' · '}
          {stripVendorPrefix(m.model)} {formatModelPct(m.count, total)}
        </span>
      ))}
      {remaining.length > 0 && (
        <span className="ml-1 text-gray-400 dark:text-gray-500">
          +{remaining.length} more
        </span>
      )}
    </span>
  );
}
